import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { money, fmtSlotDate, itemLabel } from "@/lib/format";
import { STATUS_META } from "@/lib/status";
import { Spinner } from "@/components/common/States";
import { cn } from "@/lib/utils";

// A delivery order's effective calendar date: its expected delivery date, else its slot date.
const deliveryKey = (b) => (b.expected_delivery_date || b.slot_date || "").slice(0, 10);

export default function OwnerCalendar() {
  const query = useQuery({ queryKey: ["ownerBookings", "all"], queryFn: () => ownerApi.getBookings("all") });
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const [selected, setSelected] = useState(dayjs().format("YYYY-MM-DD"));

  const byDay = useMemo(() => {
    const m = {};
    (query.data ?? []).filter((b) => b.status !== "cancelled" && !b.is_test).forEach((b) => {
      const k = deliveryKey(b);
      if (k) (m[k] = m[k] || []).push(b);
    });
    return m;
  }, [query.data]);

  const start = month.startOf("week");
  const days = Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  const dayOrders = byDay[selected] ?? [];

  return (
    <div className="space-y-4" data-testid="owner-calendar">
      <div>
        <h2 className="font-display text-lg font-bold">Delivery calendar</h2>
        <p className="text-sm text-muted-foreground">Orders by expected delivery date. Tap a day to see its load.</p>
      </div>

      {query.isPending ? <Spinner /> : (
        <>
          <div className="flex items-center justify-between">
            <button onClick={() => setMonth((m) => m.subtract(1, "month"))} data-testid="owner-cal-prev" className="flex h-9 w-9 items-center justify-center rounded-full border border-border"><ChevronLeft className="h-4 w-4" /></button>
            <p className="font-display font-bold">{month.format("MMMM YYYY")}</p>
            <button onClick={() => setMonth((m) => m.add(1, "month"))} data-testid="owner-cal-next" className="flex h-9 w-9 items-center justify-center rounded-full border border-border"><ChevronRight className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d} className="py-1 font-semibold">{d}</div>)}
            {days.map((d) => {
              const key = d.format("YYYY-MM-DD");
              const count = (byDay[key] ?? []).length;
              const inMonth = d.month() === month.month();
              const isSel = key === selected;
              return (
                <button key={key} onClick={() => setSelected(key)} data-testid={`owner-cal-day-${key}`} className={cn("relative flex h-14 flex-col items-center justify-center rounded-lg border text-sm", isSel ? "border-primary bg-primary/10" : "border-border", !inMonth && "opacity-40")}>
                  <span className={cn("font-medium", d.isSame(dayjs(), "day") && "text-primary")}>{d.date()}</span>
                  {count ? <span className="mt-0.5 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{count}</span> : null}
                </button>
              );
            })}
          </div>

          <div>
            <p className="mb-2 font-semibold">{fmtSlotDate(selected)} · {dayOrders.length} order{dayOrders.length === 1 ? "" : "s"}</p>
            {!dayOrders.length ? <p className="text-sm text-muted-foreground">No deliveries on this day.</p> : (
              <div className="space-y-2" data-testid="owner-cal-day-list">
                {dayOrders.map((b) => (
                  <div key={b.id} data-testid={`owner-cal-order-${b.code}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{b.code} · {b.customer_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{b.items.map((i) => itemLabel(i)).join(", ")}</p>
                    </div>
                    <div className="ml-2 shrink-0 text-right">
                      <p className="text-sm font-bold text-primary">{money(b.total)}</p>
                      <p className="text-[11px] text-muted-foreground">{STATUS_META[b.status]?.label || b.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
